"use client";

import { type RefObject, useEffect, useRef } from "react";
import styles from "./FirstRunGlobe.module.css";
import { globeDestination, westwardLongitude } from "./globe-destinations";
import { createGlobeRenderer } from "./globe-renderer";
import { listenForGlobeBoundaryScroll } from "./globe-scroll";

interface FirstRunGlobeProps {
  sceneRef: RefObject<HTMLDivElement | null>;
  scrollRef: RefObject<HTMLDivElement | null>;
  languageCode: string | null;
  reduce: boolean;
  paused: boolean;
}

interface Journey {
  longitude: number;
  latitude: number;
  fromLongitude: number;
  fromLatitude: number;
  targetLongitude: number;
  targetLatitude: number;
  began: number;
  travelling: boolean;
}

export function FirstRunGlobe({
  sceneRef,
  scrollRef,
  languageCode,
  reduce,
  paused,
}: FirstRunGlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const destination = globeDestination(languageCode);
  const updateRef = useRef<(() => void) | null>(null);
  const inputRef = useRef({ languageCode, reduce, paused });
  const journeyRef = useRef<Journey>({
    longitude: 20,
    latitude: -20,
    fromLongitude: 20,
    fromLatitude: -20,
    targetLongitude: 20,
    targetLatitude: -20,
    began: 0,
    travelling: false,
  });

  useEffect(() => {
    inputRef.current = { languageCode, reduce, paused };
    updateRef.current?.();
  }, [languageCode, reduce, paused]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const scene = sceneRef.current;
    const scroll = scrollRef.current;
    if (!canvas || !scene || !scroll) return;
    const renderer = createGlobeRenderer(canvas);
    const journey = journeyRef.current;
    let frame = 0;
    let scrollOffset = scroll.scrollTop;
    let scrollLongitude = 0;
    let lastScroll = -1000;
    let lastCode: string | null | undefined;
    let lastTime = 0;

    const paint = () => {
      const phone = scene.clientWidth < 640;
      const short = scene.clientHeight <= 540;
      const angle = phone ? 14.5 : 10;
      const progress = inputRef.current.reduce
        ? 0
        : Math.min(1, Math.max(0, scrollOffset / (scene.clientHeight * 0.43)));
      const chosen = Boolean(inputRef.current.languageCode);
      const expandedRadius = Math.max(550, scene.clientWidth * 1.2);
      const globeRadius = Math.min(scene.clientWidth * 0.42, scene.clientHeight * 0.25, 260);
      // Language selection reveals geography in the original composition.
      // Only deliberate scrolling pulls back to show the whole globe.
      const zoom = progress;
      const radius = expandedRadius + (globeRadius - expandedRadius) * zoom;
      const horizon = scene.clientHeight * ((short ? 0.94 : phone ? 0.82 : 0.77) - progress * 0.31);
      scene.style.setProperty("--planet-r", `${radius}px`);
      scene.style.setProperty("--horizon", `${horizon}px`);
      scene.style.setProperty("--globe-pin-x", String(Math.sin((angle * Math.PI) / 180) * 0.978));
      scene.style.setProperty(
        "--globe-pin-y",
        String(1 - Math.cos((angle * Math.PI) / 180) * 0.978)
      );
      scene.dataset.globeDestination = globeDestination(inputRef.current.languageCode)?.name ?? "";
      scene.dataset.globeLongitude = (
        (((journey.longitude + scrollLongitude) % 360) + 360) %
        360
      ).toFixed(2);
      scene.toggleAttribute(
        "data-globe-arrived",
        Boolean(
          chosen &&
          !journey.travelling &&
          Math.abs(scrollLongitude) < 0.01 &&
          Math.abs(journey.longitude - journey.targetLongitude) < 0.01 &&
          Math.abs(journey.latitude - journey.targetLatitude) < 0.01
        )
      );
      renderer?.draw(journey.longitude + scrollLongitude, journey.latitude, angle, radius, horizon);
      // Chosen languages without a geographic destination still retain the
      // normal ready state; no unrelated country is invented for them.
      if (!chosen) scene.removeAttribute("data-globe-destination");
    };

    const tick = (time: number) => {
      frame = 0;
      if (document.hidden) return;
      // Bound draw work to 30fps during movement. There is no idle render loop.
      if (time - lastTime < 30) {
        frame = requestAnimationFrame(tick);
        return;
      }
      lastTime = time;
      const { reduce: still, paused: stopped } = inputRef.current;
      if (stopped) journey.travelling = false;
      if (journey.travelling) {
        const progress = still ? 1 : Math.min(1, (time - journey.began) / 1500);
        const eased = progress * progress * (3 - 2 * progress);
        journey.longitude =
          journey.fromLongitude + (journey.targetLongitude - journey.fromLongitude) * eased;
        journey.latitude =
          journey.fromLatitude + (journey.targetLatitude - journey.fromLatitude) * eased;
        if (progress === 1) journey.travelling = false;
      }
      const moving = !still && !stopped && (journey.travelling || time - lastScroll < 160);
      scene.dataset.globeMotion = moving ? "running" : "rest";
      paint();
      if (moving) frame = requestAnimationFrame(tick);
    };

    const wake = () => {
      if (!frame && !document.hidden) frame = requestAnimationFrame(tick);
    };
    const update = () => {
      const { languageCode: code, reduce: still, paused: stopped } = inputRef.current;
      const place = globeDestination(code);
      const resume =
        !stopped &&
        !journey.travelling &&
        Math.abs(journey.longitude - journey.targetLongitude) > 0.01;
      if ((code !== lastCode || resume) && place) {
        // Absorb scroll into the camera before selection: travel begins from
        // what the learner is actually seeing, including rapid reselection.
        journey.longitude += scrollLongitude;
        scrollLongitude = 0;
        journey.fromLongitude = journey.longitude;
        journey.fromLatitude = journey.latitude;
        journey.targetLongitude = westwardLongitude(journey.longitude, place.longitude);
        // Place the destination just inside the rim, directly below Chicky.
        journey.targetLatitude = place.latitude - 78;
        journey.began = performance.now();
        journey.travelling = !still && !stopped;
        if (!journey.travelling) {
          journey.longitude = journey.targetLongitude;
          journey.latitude = journey.targetLatitude;
        }
      }
      if (still && journey.travelling) {
        journey.longitude = journey.targetLongitude;
        journey.latitude = journey.targetLatitude;
        journey.travelling = false;
      }
      if (stopped || !place) journey.travelling = false;
      lastCode = code;
      wake();
    };
    const move = (pixels: number) => {
      if (!inputRef.current.reduce && !inputRef.current.paused) {
        // Whole turns are visually identical. Bound the angle so arbitrarily
        // long exploration does not lose floating-point precision.
        scrollLongitude = (scrollLongitude - pixels * 0.38) % 360;
        lastScroll = performance.now();
      }
      wake();
    };
    const onScroll = () => {
      const next = scroll.scrollTop;
      move(next - scrollOffset);
      scrollOffset = next;
    };
    const stopBoundaryScroll = listenForGlobeBoundaryScroll(
      scroll,
      move,
      () => !inputRef.current.reduce && !inputRef.current.paused && !document.hidden
    );
    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = 0;
      } else {
        update();
      }
    };
    const contextLost = (event: Event) => {
      event.preventDefault();
      // Show the illustrated fallback, preserving all setup controls.
      canvas.style.opacity = "0";
    };
    const resized = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(wake);
    resized?.observe(scene);
    window.addEventListener("resize", wake);
    scroll.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    canvas.addEventListener("webglcontextlost", contextLost);
    updateRef.current = update;
    update();
    return () => {
      cancelAnimationFrame(frame);
      resized?.disconnect();
      window.removeEventListener("resize", wake);
      renderer?.dispose();
      scroll.removeEventListener("scroll", onScroll);
      stopBoundaryScroll();
      document.removeEventListener("visibilitychange", onVisibility);
      canvas.removeEventListener("webglcontextlost", contextLost);
      updateRef.current = null;
      for (const property of ["--planet-r", "--horizon", "--globe-pin-x", "--globe-pin-y"]) {
        scene.style.removeProperty(property);
      }
      for (const attribute of [
        "data-globe-motion",
        "data-globe-arrived",
        "data-globe-destination",
        "data-globe-longitude",
      ]) {
        scene.removeAttribute(attribute);
      }
    };
  }, [sceneRef, scrollRef]);

  return (
    <>
      <div className={styles.world} data-first-run-globe>
        <div className={styles.fallback} />
      </div>
      <canvas ref={canvasRef} className={styles.surface} />
      {destination && <span className={styles.destination}>{destination.name}</span>}
    </>
  );
}
